import type { CSSProperties, ReactNode } from "react";

export const labelCss: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 9,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  fontWeight: 500,
  color: "var(--fg-meta)",
};

export const valueCss: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 12,
  color: "var(--fg-hi)",
  fontVariantNumeric: "tabular-nums",
};

export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 92px) 1fr",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span style={labelCss}>{label}</span>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
  step,
  suffix,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        border: "1px solid var(--line-2)",
        background: "var(--bg-0)",
        height: 26,
        borderRadius: 2,
      }}
    >
      <input
        type="number"
        value={Number.isFinite(value) ? value : ""}
        min={min}
        max={max}
        step={step ?? "any"}
        onChange={(e) => {
          const n = e.target.valueAsNumber;
          onChange(Number.isFinite(n) ? n : 0);
        }}
        style={{
          flex: 1,
          minWidth: 0,
          background: "transparent",
          border: "none",
          outline: "none",
          color: "var(--fg-hi)",
          fontFamily: "var(--f-mono)",
          fontSize: 12,
          fontVariantNumeric: "tabular-nums",
          padding: "0 8px",
          textAlign: "right",
          appearance: "textfield",
          MozAppearance: "textfield",
        }}
      />
      {suffix && (
        <span
          style={{
            ...labelCss,
            padding: "0 8px",
            borderLeft: "1px solid var(--line-2)",
            height: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

export function SegmentToggle<T extends string>({
  value,
  options,
  onChange,
  activeColor,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string; color?: string }>;
  onChange: (v: T) => void;
  activeColor?: string;
}) {
  return (
    <div
      role="tablist"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${options.length}, 1fr)`,
        border: "1px solid var(--line-2)",
        borderRadius: 2,
        overflow: "hidden",
        height: 26,
      }}
    >
      {options.map((opt, i) => {
        const active = opt.value === value;
        const color = opt.color ?? activeColor ?? "var(--brand)";
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            role="tab"
            aria-selected={active}
            style={{
              background: active ? `${color}15` : "transparent",
              color: active ? color : "var(--fg-meta)",
              border: "none",
              borderLeft: i === 0 ? "none" : "1px solid var(--line-2)",
              fontFamily: "var(--f-mono)",
              fontSize: 10,
              letterSpacing: "0.14em",
              fontWeight: 500,
              textTransform: "uppercase",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function Slider({
  value,
  onChange,
  min,
  max,
  step,
  color,
}: {
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step?: number;
  color?: string;
}) {
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step ?? 1}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{
        width: "100%",
        accentColor: color ?? "var(--brand)",
      }}
    />
  );
}

export function ReadoutRow({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <span style={labelCss}>{label}</span>
      <span style={{ ...valueCss, color: color ?? "var(--fg-hi)" }}>
        {value}
      </span>
    </div>
  );
}

export const sectionStyle: CSSProperties = {
  padding: "14px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  borderBottom: "1px solid var(--line-1)",
};

export const readoutSectionStyle: CSSProperties = {
  ...sectionStyle,
  gap: 8,
  borderBottom: "none",
};
